import React, { memo } from "react";
import { Tooltip } from "antd";
import {
  FontSizeOutlined,
  AlignLeftOutlined,
  NumberOutlined,
  CalendarOutlined,
  FieldTimeOutlined,
  ClockCircleOutlined,
  CheckSquareOutlined,
  DownSquareOutlined,
  PictureOutlined,
  PaperClipOutlined,
  CalculatorOutlined,
  UserOutlined,
} from "@ant-design/icons";

export interface FieldTypeMeta {
  normalizedType: string;
  label: string;
  color: string;
  tagColor: string;
  IconComponent: React.ComponentType<any>;
}

export const getFieldTypeMeta = (type?: string, key?: string): FieldTypeMeta => {
  let t = (type || "").toLowerCase().trim();
  const k = (key || "").toLowerCase();

  // If type is empty or generic, try inferring from key
  if (!t || t === "text" || t === "string") {
    if (k.includes("datetime") || k.includes("created_at_ts") || k.includes("timestamp") || k.endsWith("_ts")) t = "datetime";
    else if (k.includes("date") || k.includes("created") || k.includes("updated")) t = "date";
    else if (k.includes("time")) t = "time";
    else if (k === "user_id" || k.includes("user") || k.includes("collector")) t = "user";
    else if (k.includes("photo") || k.includes("image") || k.includes("picture")) t = "photo";
  }

  switch (t) {
    case "user":
      return {
        normalizedType: "user",
        label: "Người dùng (user)",
        color: "#6366f1",
        tagColor: "purple",
        IconComponent: UserOutlined,
      };

    case "number":
    case "integer":
    case "float":
    case "double":
      return {
        normalizedType: "number",
        label: "Số (number)",
        color: "#2563eb",
        tagColor: "blue",
        IconComponent: NumberOutlined,
      };

    case "datetime":
      return {
        normalizedType: "datetime",
        label: "Ngày giờ (datetime)",
        color: "#059669",
        tagColor: "green",
        IconComponent: FieldTimeOutlined,
      };

    case "date":
      return {
        normalizedType: "date",
        label: "Ngày tháng (date)",
        color: "#10b981",
        tagColor: "green",
        IconComponent: CalendarOutlined,
      };

    case "time":
      return {
        normalizedType: "time",
        label: "Thời gian (time)",
        color: "#0891b2",
        tagColor: "cyan",
        IconComponent: ClockCircleOutlined,
      };

    case "boolean":
    case "bool":
      return {
        normalizedType: "boolean",
        label: "Đúng/Sai (boolean)",
        color: "#7c3aed",
        tagColor: "purple",
        IconComponent: CheckSquareOutlined,
      };

    case "select":
    case "multiselect":
    case "radio":
    case "dropdown":
    case "cascadeselect":
      return {
        normalizedType: "select",
        label: "Lựa chọn (select)",
        color: "#d97706",
        tagColor: "orange",
        IconComponent: DownSquareOutlined,
      };

    case "textarea":
      return {
        normalizedType: "textarea",
        label: "Đoạn văn (textarea)",
        color: "#475569",
        tagColor: "default",
        IconComponent: AlignLeftOutlined,
      };

    case "photo":
    case "image":
      return {
        normalizedType: "photo",
        label: "Hình ảnh (photo)",
        color: "#db2777",
        tagColor: "magenta",
        IconComponent: PictureOutlined,
      };

    case "file":
    case "attachment":
      return {
        normalizedType: "file",
        label: "Tập tin (file)",
        color: "#ea580c",
        tagColor: "volcano",
        IconComponent: PaperClipOutlined,
      };

    case "computed":
      return {
        normalizedType: "computed",
        label: "Công thức tính (computed)",
        color: "#4f46e5",
        tagColor: "geekblue",
        IconComponent: CalculatorOutlined,
      };

    case "text":
    default:
      return {
        normalizedType: "text",
        label: "Văn bản (text)",
        color: "#64748b",
        tagColor: "default",
        IconComponent: FontSizeOutlined,
      };
  }
};

export interface FieldTypeIconProps {
  type?: string;
  fieldKey?: string;
  showTooltip?: boolean;
  style?: React.CSSProperties;
  className?: string;
}

export const FieldTypeIcon = memo(({
  type,
  fieldKey,
  showTooltip = true,
  style,
  className,
}: FieldTypeIconProps) => {
  const meta = getFieldTypeMeta(type, fieldKey);
  const Icon = meta.IconComponent;

  const iconElement = (
    <span
      className={className}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        color: meta.color,
        fontSize: "12px",
        marginRight: 4,
        flexShrink: 0,
        ...style,
      }}
    >
      <Icon />
    </span>
  );

  if (!showTooltip) {
    return iconElement;
  }

  return (
    <Tooltip title={`Kiểu: ${meta.label}`} placement="top">
      {iconElement}
    </Tooltip>
  );
});

export default FieldTypeIcon;
